<template>
    <div v-if="show" class="modal-overlay" @click.self="$emit('close')">
        <div class="payment-modal">
            <div class="modal-header">
                <h2>Encaissement de la vente</h2>
                <button class="close-btn" @click="$emit('close')">×</button>
            </div>

            <div class="modal-body">
                <div v-if="errorMessage">
                    <div class="alert alert-danger">
                        <i class="fa-solid fa-circle-exclamation"></i>
                        <span>{{ errorMessage }}</span>
                    </div>
                </div>
                <!-- Informations de la vente -->
                <div class="sale-info-section">
                    <h3>Détails de la vente</h3>
                    <div class="sale-details">
                        <div class="detail-row">
                            <span class="label">Facture</span>
                            <span class="value">{{ sale.invoice_number }}</span>
                        </div>
                        <div class="detail-row">
                            <span class="label">Client</span>
                            <span class="value">{{ sale.customer?.name || 'Client anonyme' }}</span>
                        </div>
                        <div class="detail-row">
                            <span class="label">Date de vente</span>
                            <span class="value">{{ formatDate(sale.sale_date) }}</span>
                        </div>
                        <div class="detail-row highlight">
                            <span class="label">Montant à encaisser</span>
                            <span class="value amount">{{ formatCurrency(sale.total_incl_tax) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Méthode de paiement -->
                <div class="payment-method-section">
                    <h3>Méthode de paiement <span class="required">*</span></h3>
                    <div class="payment-methods">
                        <button v-for="method in paymentMethods" :key="method.id"
                            :class="['payment-method-btn', { active: paymentData.payment_type === method.id }]"
                            @click="selectPaymentMethod(method.id)">
                            <i class="method-icon" :class="method.icon"></i>
                            <span class="method-name">{{ method.name }}</span>
                        </button>
                    </div>
                </div>

                <!-- Date de paiement -->
                <div class="form-group">
                    <label>Date de paiement</label>
                    <input type="datetime-local" v-model="paymentData.payment_date">
                </div>

                <!-- Notes -->
                <div class="form-group">
                    <label>Notes (optionnel)</label>
                    <textarea v-model="paymentData.notes" rows="3" placeholder="Notes sur le paiement..."
                        maxlength="500"></textarea>
                </div>

                <!-- Récapitulatif -->
                <div class="summary-section">
                    <div class="summary-row">
                        <span>Montant</span>
                        <span class="summary-amount">{{ formatCurrency(sale.total_incl_tax) }}</span>
                    </div>
                    <div v-if="paymentData.payment_type" class="summary-row">
                        <span>Méthode</span>
                        <span>{{ getMethodName(paymentData.payment_type) }}</span>
                    </div>
                </div>
            </div>

            <div class="modal-footer">
                <button @click="$emit('close')" class="btn-secondary" :disabled="isProcessing">
                    Annuler
                </button>
                <button @click="processPayment" class="btn-primary" :disabled="!canProcess || isProcessing">
                    <span v-if="isProcessing" class="spinner"></span>
                    {{ isProcessing ? 'Traitement...' : 'Confirmer le paiement' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import paymentService from '@/services/paymentService';
import CashRegisterService from '@/services/cashRegisterService';

export default {
    name: 'PaymentModal',
    props: {
        show: {
            type: Boolean,
            required: true
        },
        sale: {
            type: Object,
            required: true
        }
    },
    emits: ['close', 'payment-success'],
    setup(props, { emit }) {
        const isProcessing = ref(false);
        const paymentData = ref({
            sale_id: '',
            payment_type: '',
            payment_date: '',
            cash_register_id: '',
            notes: ''
        });
        const errorMessage = ref('');

        const paymentMethods = [
            { id: 'cash', name: 'Espèces', icon: 'fa-solid fa-money-bill' },
            { id: 'card', name: 'Carte bancaire', icon: 'fa-solid fa-credit-card' },
            { id: 'mobile_money', name: 'Mobile Money', icon: 'fa-solid fa-mobile' },
            { id: 'bank_transfer', name: 'Virement bancaire', icon: 'fa-solid fa-bank' },
            { id: 'check', name: 'Chèque', icon: 'fa-solid fa-file-invoice-dollar' }
        ];

        const canProcess = computed(() => {
            return paymentData.value.payment_type &&
                props.sale.id;
        });

        const selectPaymentMethod = (methodId) => {
            paymentData.value.payment_type = methodId;
        };

        const getMethodName = (methodId) => {
            const method = paymentMethods.find(m => m.id === methodId);
            return method ? method.name : '';
        };

        const initializePaymentData = () => {
            const now = new Date();
            const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
                .toISOString()
                .slice(0, 16);

            paymentData.value = {
                sale_id: props.sale.id,
                payment_type: '',
                payment_date: localDateTime,
                cash_register_id: '',
                notes: `Paiement pour la vente ${props.sale.invoice_number}`
            };
        };

        const processPayment = async () => {
            if (!canProcess.value) return;

            try {
                const cashRegister = await CashRegisterService.getMyOpenedCashRegister();
                paymentData.value.cash_register_id = cashRegister.data.id;
            } catch (error) {
                console.error('Erreur lors de la récupération de la caisse:', error);
                errorMessage.value = error.message;
                return;
            }

            try {
                isProcessing.value = true;

                // Préparer les données pour l'API
                const requestData = {
                    sale_id: paymentData.value.sale_id,
                    payment_type: paymentData.value.payment_type,
                    cash_register_id: paymentData.value.cash_register_id,
                    payment_date: paymentData.value.payment_date || undefined,
                    notes: paymentData.value.notes || undefined
                };

                console.log('TODO: Envoyer les données de paiement', requestData);

                await paymentService.makePayment(requestData);

                emit('payment-success');
                emit('close');
            } catch (error) {
                console.error('Erreur lors du paiement:', error);
                // TODO: Afficher une notification d'erreur
            } finally {
                isProcessing.value = false;
            }
        };

        const formatCurrency = (amount) => {
            return new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'XOF',
                minimumFractionDigits: 0
            }).format(amount || 0);
        };

        const formatDate = (dateString) => {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        };

        watch(() => props.show, (newVal) => {
            if (newVal) {
                initializePaymentData();
            }
        });

        return {
            isProcessing,
            paymentData,
            errorMessage,
            paymentMethods,
            canProcess,
            selectPaymentMethod,
            getMethodName,
            processPayment,
            formatCurrency,
            formatDate
        };
    }
};
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
}

.payment-modal {
    background: white;
    border-radius: 12px;
    width: 100%;
    max-width: 70%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
    border-bottom: 1px solid #e5e7eb;
}

.modal-header h2 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
}

.close-btn {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #6b7280;
    cursor: pointer;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    transition: all 0.2s;
}

.close-btn:hover {
    background: #f3f4f6;
}

.modal-body {
    padding: 1.5rem;
    overflow-y: auto;
    flex: 1;
}

.sale-info-section,
.payment-method-section {
    margin-bottom: 1.5rem;
}

.sale-info-section h3,
.payment-method-section h3 {
    font-size: 0.9rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 1rem 0;
}

.required {
    color: #dc2626;
}

.sale-details {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 1rem;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem 0;
    border-bottom: 1px solid #e5e7eb;
}

.detail-row:last-child {
    border-bottom: none;
}

.detail-row.highlight {
    background: #f3f4f6;
    margin: 0.5rem -1rem 0;
    padding: 0.75rem 1rem;
    border-radius: 0 0 6px 6px;
    border-bottom: none;
}

.detail-row .label {
    font-size: 0.85rem;
    color: #6b7280;
}

.detail-row .value {
    font-size: 0.85rem;
    font-weight: 500;
    color: #111827;
}

.detail-row .value.amount {
    font-size: 1.1rem;
    font-weight: 700;
    color: #0A5C6B;
}

.payment-methods {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;
}

.payment-method-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem 0.5rem;
    background: white;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
}

.payment-method-btn:hover {
    background: #f9fafb;
    border-color: #d1d5db;
}

.payment-method-btn.active {
    border-color: #0A5C6B;
    background: #f0fdfa;
}

.method-icon {
    font-size: 1.5rem;
}

.method-name {
    font-size: 0.75rem;
    font-weight: 500;
    color: #374151;
    text-align: center;
}

.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    display: block;
    font-size: 0.85rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.5rem;
}

.form-group input,
.form-group select,
.form-group textarea {
    width: 100%;
    padding: 0.625rem;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 0.9rem;
    background: white;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
    outline: none;
    border-color: #0A5C6B;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.form-group textarea {
    resize: vertical;
}

.summary-section {
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 1rem;
    margin-top: 1.5rem;
}

.summary-row {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem 0;
    font-size: 0.9rem;
}

.summary-row:first-child {
    padding-top: 0;
}

.summary-row:last-child {
    padding-bottom: 0;
}

.summary-amount {
    font-size: 1.1rem;
    font-weight: 700;
    color: #0A5C6B;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1.5rem;
    border-top: 1px solid #e5e7eb;
}

.btn-secondary,
.btn-primary {
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.btn-secondary {
    background: white;
    color: #6b7280;
    border: 1px solid #e5e7eb;
}

.btn-secondary:hover:not(:disabled) {
    background: #f9fafb;
}

.btn-primary {
    background: #0A5C6B;
    color: white;
}

.btn-primary:hover:not(:disabled) {
    background: #094854;
}

.btn-secondary:disabled,
.btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 768px) {
    .payment-methods {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 480px) {
    .payment-methods {
        grid-template-columns: 1fr;
    }
}
</style>